import { useDispatch } from 'react-redux'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import RadioButtons from '../../../../../components/forms/RadioButtons'
import Checkbox from '../../../../../components/forms/Checkbox'
import { orderBy, uniqBy } from 'lodash'
import SimpleDatePicker from '../../../../../components/forms/SimpleDatePicker'
import moment from 'moment'
import Button from '../../../../../components/forms/Button'
import { StyledFieldSet, StyledLegend } from '../../../../../components/StyledElements'
import WaitAction from '../../../../../wait/WaitAction'
import { arrayOf } from '../../../../../utils/StoreUtils'
import { getUser } from 'utils/SettingUtils'
import { Card, CardContent, Grid } from '@mui/material'
import Select from '../../../../../components/forms/Select'
import DtoMeasureStats from '../../../../../station/dto/piezometer/DtoMeasureStats'
import { CardTitle } from '../../../../../components/card/NewCard'
import { HYDRO_FOLLOW_L_STORAGE, HYDRO_TYPEID } from '../../../../constants/HydrometryConstants'
import HydrometerStationAction from '../../../../../station/actions/HydrometerStationAction'
import { getFrequencyStats } from '../../utils/SuiviStatsUtilsHydro'
import { buildHydroStatsSeries } from '../../utils/StatsSeriesUtilsHydro'
import { STATISTICS } from '../../constants/HydroSuiviConstants'
import DtoHydrometerChartOptions from '../../../../../station/dto/hydrometricStation/DtoHydrometerChartOptions'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import { IconButtonMUI } from 'components/styled/Buttons'

const HydroSuiviStatTab = ({
    id,
    histoYears,
    hasValidMeasures, // true si il a des données valides
    changeParent = () => { }, // met à jour les state du parent (dont les séries liées à cette tab)
    hydrometerChartOptions,
    tab,
    typeId,
    hydroStatistics = [],
    changeTypeId = () => { },
    minY,
}) => {
    const dispatch = useDispatch()

    const typeIdOption = hydrometerChartOptions.find(opt => parseInt(opt.dataType ?? HYDRO_TYPEID.HEIGHT.toString()) === typeId)

    const [statSeries, setStatSeries] = useLocalStorage(HYDRO_FOLLOW_L_STORAGE.STAT_SERIES_HYDROMETRIC_FOLLOW, ['ENVELOPPE', 'ENVELOPPE_AVERAGE'])
    const [tmpStatSeries, setTmpStatSeries] = useState(statSeries)
    const [readyApply, setReadyApply] = useState(false)
    const [validOnly, setValidOnly] = useState(hasValidMeasures)
    const [linearMode, setLinearMode] = useLocalStorage(HYDRO_FOLLOW_L_STORAGE.LINEAR_MODE_HYDROMETRIC_FOLLOW)
    const [tmpLinearMode, setTmpLinearMode] = useState(linearMode)
    const [startDate, setStartDate] = useState(typeIdOption?.statsCalculationStartDate)
    const [endDate, setEndDate] = useState(typeIdOption?.statsCalculationEndDate)
    const changeStat = (bool, stat) => {
        if (bool) {
            setTmpStatSeries([...tmpStatSeries, stat])
        } else {
            setTmpStatSeries(tmpStatSeries.filter(s => s !== stat))
        }
        setReadyApply(true)
    }

    const change = (v, func) => {
        func(v)
        setReadyApply(true)
    }

    const saveOptions = () => {
        const newOptions = [...hydrometerChartOptions.filter(opt => parseInt(opt.dataType ?? -1) !== typeId), { ...typeIdOption, statsCalculationStartDate: startDate, statsCalculationEndDate: endDate }]
        dispatch(HydrometerStationAction.updateHydrometerChartOptions(id, newOptions))
    }

    const recalculateStats = useCallback(() => {
        dispatch(WaitAction.waitStart())
        buildHydroStatsSeries({
            id,
            typeId,
            statSeries,
            histoYears,
            validOnly,
            startDate,
            endDate,
            linearMode,
            minY,
        }).then(series => {
            changeParent({ statsSeries: series })
            setReadyApply(false)
            dispatch(WaitAction.waitStop())
        })
    }, [changeParent, dispatch, endDate, histoYears, id, linearMode, minY, startDate, statSeries, typeId, validOnly])

    useEffect(() => {
        setValidOnly(hasValidMeasures)
        setStatSeries([...statSeries]) // to trigger next useEffect, if there isn't any change of statSeries state, stats won't be shown on the graph
        setTmpStatSeries(statSeries)
    }, [typeId, hasValidMeasures])

    useEffect(() => {
        recalculateStats()
    }, [statSeries, linearMode])

    useEffect(() => {
        setTmpLinearMode(linearMode)
    }, [linearMode])

    const statsUsed = useMemo(() => uniqBy(orderBy(hydroStatistics, ['order', 'typeId'], 'asc').map(h => ({
        id: h.typeId,
        label: h.label,
    })), 'id'), [hydroStatistics])

    useEffect(() => {
        if (statsUsed.length && !statsUsed.some(stat => stat.id === typeId)) {
            changeTypeId(statsUsed[0].id)
        }
    }, [statsUsed, typeId])

    const freChecks = getFrequencyStats(histoYears).map(period => (
        <Grid container key={period.name}>
            <Grid size={12}>
                <Checkbox label={ period.name } checked={ tmpStatSeries.includes(period.code.replace('fre', 'PERIOD')) } onChange={ v => changeStat(v, period.code.replace('fre', 'PERIOD')) }/>
            </Grid>
        </Grid>
    ))
    const validRadioElements = [
        { code: true, name: i18n.yes },
        { code: false, name: i18n.no },
    ]
    const disabled = getUser().consultant === '1'
    if (tab !== STATISTICS) {
        return null
    }
    return (
        <Card className='margin-top-1'>
            <CardTitle title={i18n.statistics} />
            <CardContent>
                <div className='padding-1'>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.dataType}</StyledLegend>
                        <Select
                            col={10}
                            value={typeId}
                            options={statsUsed}
                            onChange={v => changeTypeId(v)}
                            noNullValue
                            keyValue='id'
                            noSort
                            integerValue
                        />
                    </StyledFieldSet>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.options}</StyledLegend>
                        <Grid container size={12} spacing={1} alignItems='center'>
                            <Grid size={12}>
                                <RadioButtons
                                    col={12}
                                    elements={validRadioElements}
                                    selected={validOnly}
                                    onChange={v => change(v, setValidOnly)}
                                    disabled={!hasValidMeasures}
                                    title={i18n.calcValidDataOnly}
                                />
                            </Grid>
                            <Grid size={12}>
                                <h5>Dates des données à utiliser pour calculer les statistiques</h5>
                                <h6>Si la date de fin est vide, l'année en cours ne sera pas prise en compte pour les
                                    calculs des statistiques.</h6>
                            </Grid>
                            <Grid size={4}>
                                <SimpleDatePicker value={startDate} label={i18n.startDate}
                                    onChange={v => change(v, setStartDate)} disabled={disabled}
                                />
                            </Grid>
                            <Grid size={4}>
                                <SimpleDatePicker value={endDate} label={i18n.endDate}
                                    onChange={v => change(v, setEndDate)} max={moment().startOf('year').valueOf()}
                                    disabled={disabled}
                                />
                            </Grid>
                            {getUser().consultant !== '1' && <Grid size={4}><Button title={i18n.register} onClick={saveOptions} /></Grid>}
                            <Grid size={12}>
                                <Checkbox
                                    checked={tmpLinearMode}
                                    onChange={v => {
                                        setReadyApply(true)
                                        setTmpLinearMode(v)
                                    }}
                                    label={i18n.linearMode}
                                />
                            </Grid>
                        </Grid>
                    </StyledFieldSet>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.statistics}</StyledLegend>
                        <Grid container>
                            <Grid size={12}>
                                <Checkbox label={i18n.enveloppeMinMax} checked={tmpStatSeries.includes('ENVELOPPE')}
                                    onChange={v => changeStat(v, 'ENVELOPPE')}
                                />
                            </Grid>
                            <Grid size={12}>
                                <Checkbox label={i18n.averageStatistics}
                                    checked={tmpStatSeries.includes('ENVELOPPE_AVERAGE')}
                                    onChange={v => changeStat(v, 'ENVELOPPE_AVERAGE')}
                                />
                            </Grid>
                            <Grid size={12}>
                                <Checkbox label={i18n.median} checked={tmpStatSeries.includes('ENVELOPPE_MEDIAN')}
                                    onChange={v => changeStat(v, 'ENVELOPPE_MEDIAN')}
                                />
                            </Grid>
                        </Grid>
                    </StyledFieldSet>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.frequencyStats}</StyledLegend>
                        {freChecks}
                    </StyledFieldSet>
                    <Grid container justifyContent='center' sx={{ bottom: 0, position: 'sticky', zIndex: 200 }}>
                        <IconButtonMUI
                            tooltip={i18n.apply}
                            onClick={() => {
                                setStatSeries(tmpStatSeries)
                                setLinearMode(tmpLinearMode)
                            }}
                            icon='border_color'
                            className={`${readyApply ? 'pulse' : ''}`}
                        />
                    </Grid>
                </div>
            </CardContent>
        </Card>
    )
}

HydroSuiviStatTab.propTypes = {
    tab: PropTypes.string,
    typeId: PropTypes.number,
    id: PropTypes.number,
    hydrometerChartOptions: arrayOf(DtoHydrometerChartOptions),
    histoYears: PropTypes.number,
    hasValidMeasures: PropTypes.bool,
    changeParent: PropTypes.func,
    hydroStatistics: arrayOf(DtoMeasureStats),
    changeTypeId: PropTypes.func,
    minY: PropTypes.number,
}

export default HydroSuiviStatTab